import { useDialogStore } from '@/store/dialogStore';
import { useState, useEffect, useCallback } from 'react';
import {
  Plus,
  X,
  Edit2,
  Trash2,
  GripVertical,
  Type,
  Calendar,
  List,
  Save,
  ToggleLeft,
  Hash,
} from 'lucide-react';
import { CompactDropdown } from '@/components/shared/inline-editors/CompactDropdown';
import type {
  CustomField,
  CustomFieldType,
  CustomFieldScope,
  CustomFieldOption,
} from '@/types';
import {
  fetchCustomFields,
  createCustomField,
  updateCustomField,
  deleteCustomField,
  reorderCustomFields,
} from '@/db/customFields';
import { toast } from '@/utils/toast';
import { handleError } from '@/utils/errorHandler';
import clsx from 'clsx';

interface CustomFieldsManagerProps {
  scope: CustomFieldScope;
  scopeId?: string;
  onFieldsChange?: () => void;
  filterType?: CustomFieldType; // Filter to show only specific type
  excludeTypes?: CustomFieldType[]; // Exclude these types from display and creation
  readOnly?: boolean; // If true, fields cannot be edited/deleted/created
}

const FIELD_TYPE_OPTIONS: {
  value: CustomFieldType;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
}[] = [
    { value: 'text', label: 'Testo', icon: Type },
    { value: 'number', label: 'Numero', icon: Hash },
    { value: 'date', label: 'Data', icon: Calendar },
    { value: 'dropdown', label: 'Dropdown', icon: List },
    { value: 'boolean', label: 'Checkbox', icon: ToggleLeft },
  ];

export function CustomFieldsManager({
  scope,
  scopeId,
  onFieldsChange,
  filterType,
  excludeTypes,
  readOnly = false,
}: CustomFieldsManagerProps) {
  const [fields, setFields] = useState<CustomField[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingField, setEditingField] = useState<CustomField | null>(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);

  const loadFields = useCallback(async () => {
    setLoading(true);
    try {
      const fetchedFields = await fetchCustomFields(scope, scopeId);
      // Filter by type
      const filtered = filterType
        ? fetchedFields.filter((f) => f.type === filterType)
        : excludeTypes?.length
          ? fetchedFields.filter((f) => !excludeTypes.includes(f.type))
          : fetchedFields;
      setFields(filtered);
    } catch (error) {
      handleError(error, { level: 'warning', message: 'Errore nel caricamento dei campi personalizzati' });
    } finally {
      setLoading(false);
    }
  }, [scope, scopeId, filterType, excludeTypes]);

  useEffect(() => {
    loadFields();
  }, [loadFields]);

  const handleCreate = async (
    fieldData: Omit<CustomField, 'id'>
  ) => {
    try {
      // Use the scope from fieldData (which comes from the form) or fallback to props
      const finalScope = fieldData.scope || scope;
      const finalScopeId =
        finalScope === 'organization' ? null : (fieldData.scope_id ?? scopeId ?? undefined);

      await createCustomField({
        ...fieldData,
        scope: finalScope,
        scope_id: finalScopeId,
      });
      await loadFields();
      setShowAddForm(false);
      onFieldsChange?.();
    } catch (error) {
      handleError(error, { level: 'error', message: 'Errore nella creazione del campo personalizzato' });
      toast(
        `Errore nella creazione del campo: ${error instanceof Error ? error.message : 'Errore sconosciuto'}`,
        'error'
      );
    }
  };

  const handleUpdate = async (id: string, updates: Partial<CustomField>) => {
    try {
      await updateCustomField(id, updates);
      await loadFields();
      setEditingField(null);
      onFieldsChange?.();
    } catch (error) {
      handleError(error, { level: 'error', message: 'Errore nell\'aggiornamento del campo personalizzato' });
    }
  };

  const handleDelete = async (id: string) => {
    const confirmed = await useDialogStore.getState().confirm({ title: 'Elimina campo', message: 'Sei sicuro di voler eliminare questo campo?', confirmLabel: 'Elimina', variant: 'danger' });
    if (!confirmed) return;
    try {
      await deleteCustomField(id);
      await loadFields();
      onFieldsChange?.();
    } catch (error) {
      handleError(error, { level: 'error', message: 'Errore nell\'eliminazione del campo personalizzato' });
    }
  };

  const handleReorder = async (newOrder: CustomField[]) => {
    try {
      const ids = newOrder.map((f) => f.id);
      await reorderCustomFields(ids);
      setFields(newOrder);
      onFieldsChange?.();
    } catch (error) {
      handleError(error, { level: 'error', message: 'Errore nel riordinamento dei campi personalizzati' });
    }
  };

  const handleDragStart = (index: number) => {
    setDraggedIndex(index);
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    if (draggedIndex === null) return;

    if (draggedIndex !== index) {
      const newFields = [...fields];
      const [removed] = newFields.splice(draggedIndex, 1);
      newFields.splice(index, 0, removed);
      setFields(newFields);
      setDraggedIndex(index);
    }
  };

  const handleDragEnd = () => {
    if (draggedIndex !== null) {
      handleReorder(fields);
      setDraggedIndex(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <span className="text-[11px] text-text-quaternary">Caricamento...</span>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Fields List */}
      <div className="space-y-2">
        {fields.length === 0 && !showAddForm && (
          <div className="text-center py-4 text-[11px] text-text-quaternary">
            Nessun campo personalizzato.
          </div>
        )}

        {fields.map((field, index) => (
          <div
            key={field.id}
            draggable={!readOnly}
            onDragStart={readOnly ? undefined : () => handleDragStart(index)}
            onDragOver={readOnly ? undefined : (e) => handleDragOver(e, index)}
            onDragEnd={readOnly ? undefined : handleDragEnd}
            className={clsx(
              'flex items-center gap-2 p-3 rounded-lg border bg-surface-tertiary/50',
              draggedIndex === index
                ? 'opacity-50'
                : 'border-border-primary hover:border-border-secondary',
              readOnly && 'cursor-default'
            )}
          >
            {!readOnly && <GripVertical className="w-4 h-4 text-text-quaternary cursor-grab" />}
            {readOnly && <div className="w-4 h-4" />}
            {editingField?.id === field.id && !readOnly ? (
              <FieldEditForm
                field={field}
                onSave={(updates) => handleUpdate(field.id, updates)}
                onCancel={() => setEditingField(null)}
                defaultType={filterType}
                defaultScope={scope}
                defaultScopeId={scopeId}
                excludeTypes={excludeTypes}
              />
            ) : (
              <>
                <div className="flex-1 flex items-center gap-2">
                  {(() => {
                    const TypeIcon =
                      FIELD_TYPE_OPTIONS.find((t) => t.value === field.type)?.icon || Type;
                    return <TypeIcon className="w-4 h-4 text-text-tertiary" />;
                  })()}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-[12px] font-medium text-text-primary">
                        {field.name}
                      </span>
                      <span className="text-[10px] text-text-quaternary px-1.5 py-0.5 rounded bg-surface-active">
                        {FIELD_TYPE_OPTIONS.find((t) => t.value === field.type)?.label}
                      </span>
                    </div>
                    {field.options && field.options.length > 0 && (
                      <div className="text-[10px] text-text-quaternary mt-0.5">
                        {field.options.length} opzioni
                      </div>
                    )}
                  </div>
                </div>
                {!readOnly && (
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => setEditingField(field)}
                      className="p-1.5 rounded hover:bg-surface-hover transition-colors"
                      title="Modifica"
                    >
                      <Edit2 className="w-3.5 h-3.5 text-text-quaternary" />
                    </button>
                    <button
                      onClick={() => handleDelete(field.id)}
                      className="p-1.5 rounded hover:bg-surface-hover transition-colors"
                      title="Elimina"
                    >
                      <Trash2 className="w-3.5 h-3.5 text-text-quaternary hover:text-accent-rose" />
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        ))}
      </div>

      {/* Add Form */}
      {showAddForm && !readOnly && (
        <div className="mt-4">
          <FieldEditForm
            onSave={handleCreate}
            onCancel={() => setShowAddForm(false)}
            defaultType={filterType}
            defaultScope={scope}
            defaultScopeId={scopeId}
            excludeTypes={excludeTypes}
          />
        </div>
      )}

      {/* Add Button - Always show when form is not open and not read-only */}
      {!showAddForm && !readOnly && (
        <button
          onClick={() => setShowAddForm(true)}
          className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg border border-dashed border-border-secondary text-[11px] text-text-tertiary hover:text-text-secondary hover:border-text-tertiary transition-all mt-2"
        >
          <Plus className="w-4 h-4" />
          Aggiungi campo
        </button>
      )}
    </div>
  );
}

interface FieldEditFormProps {
  field?: CustomField;
  onSave: (data: Omit<CustomField, 'id'>) => void;
  onCancel: () => void;
  defaultType?: CustomFieldType;
  defaultScope?: CustomFieldScope;
  defaultScopeId?: string | null;
  excludeTypes?: CustomFieldType[];
}

function FieldEditForm({
  field,
  onSave,
  onCancel,
  defaultType,
  defaultScope,
  defaultScopeId,
  excludeTypes,
}: FieldEditFormProps) {
  const [name, setName] = useState(field?.name || '');
  const [type, setType] = useState<CustomFieldType>(field?.type || defaultType || 'text');
  const [options, setOptions] = useState<CustomFieldOption[]>(field?.options || []);
  const [multiSelect, setMultiSelect] = useState(field?.icon === 'multi');
  const [newOptionLabel, setNewOptionLabel] = useState('');

  const handleAddOption = () => {
    const label = newOptionLabel.trim();
    if (!label) return;
    const value = label.toLowerCase().replace(/\s+/g, '_');
    setOptions([...options, { value, label }]);
    setNewOptionLabel('');
  };

  const handleRemoveOption = (index: number) => {
    setOptions(options.filter((_, i) => i !== index));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;

    onSave({
      name: name.trim(),
      type,
      scope: field?.scope || defaultScope || 'organization',
      scope_id: field?.scope_id ?? defaultScopeId ?? null,
      color: undefined,
      icon: type === 'dropdown' && multiSelect ? 'multi' : undefined,
      options: type === 'dropdown' ? options : undefined,
      order_index: field?.order_index ?? 0,
      created_at: field?.created_at || new Date().toISOString(),
      created_at_ms: field?.created_at_ms ?? (field?.created_at ? new Date(field.created_at).getTime() : Date.now()),
    } as Omit<CustomField, 'id'>);
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="p-3 rounded-lg border border-accent-violet/30 bg-accent-violet/5 space-y-3"
    >
      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className="block text-[10px] font-medium text-text-secondary mb-1">
            Nome campo
          </label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Es. Categoria"
            className="w-full px-2 py-1.5 rounded-lg bg-surface-tertiary border border-border-primary text-[11px] text-text-primary focus:border-accent-violet focus:ring-1 focus:ring-accent-violet"
            required
          />
        </div>
        {!defaultType && (
          <div>
            <label className="block text-[10px] font-medium text-text-secondary mb-1">Tipo</label>
            <CompactDropdown
              value={type}
              onChange={(v) => { if (v) setType(v as CustomFieldType); }}
              options={FIELD_TYPE_OPTIONS
                .filter((opt) => !excludeTypes?.includes(opt.value))
                .map((opt) => {
                  const Icon = opt.icon;
                  return { value: opt.value, label: opt.label, icon: <Icon className="w-3.5 h-3.5" /> };
                })}
              placeholder="Seleziona tipo..."
              size="sm"
              searchable={false}
              allowClear={false}
              className="w-full"
            />
          </div>
        )}
      </div>

      {type === 'dropdown' && (
        <div className="space-y-3">
          <button
            type="button"
            onClick={() => setMultiSelect(!multiSelect)}
            className="flex items-center gap-2 cursor-pointer group"
          >
            <div className={clsx(
              'w-7 h-4 rounded-full transition-colors relative',
              multiSelect ? 'bg-accent-violet' : 'bg-border-secondary'
            )}>
              <div className={clsx(
                'absolute top-0.5 w-3 h-3 rounded-full bg-white shadow-sm transition-transform',
                multiSelect ? 'translate-x-3.5' : 'translate-x-0.5'
              )} />
            </div>
            <span className="text-[11px] text-text-secondary group-hover:text-text-primary transition-colors">Selezione multipla</span>
          </button>
          <div>
          <label className="block text-[10px] font-medium text-text-secondary mb-1">Opzioni</label>
          <div className="space-y-1.5">
            {options.map((opt, index) => (
              <div key={index} className="flex items-center gap-2">
                <input
                  type="text"
                  value={opt.label}
                  onChange={(e) => {
                    const newOptions = [...options];
                    newOptions[index] = { ...newOptions[index], label: e.target.value };
                    setOptions(newOptions);
                  }}
                  className="flex-1 px-2 py-1 rounded-lg bg-surface-tertiary border border-border-primary text-[11px] text-text-primary"
                />
                <button
                  type="button"
                  onClick={() => handleRemoveOption(index)}
                  className="p-1 rounded hover:bg-surface-hover"
                >
                  <X className="w-3 h-3 text-text-quaternary hover:text-accent-rose" />
                </button>
              </div>
            ))}
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={newOptionLabel}
                onChange={(e) => setNewOptionLabel(e.target.value)}
                placeholder="Nuova opzione..."
                className="flex-1 px-2 py-1 rounded-lg bg-surface-tertiary border border-border-primary text-[11px] text-text-primary placeholder:text-text-quaternary"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAddOption();
                  }
                }}
              />
              <button
                type="button"
                onClick={handleAddOption}
                className="p-1 rounded hover:bg-surface-hover"
              >
                <Plus className="w-3 h-3 text-accent-violet" />
              </button>
            </div>
          </div>
          </div>
        </div>
      )}

      <div className="flex items-center gap-2">
        <button
          type="submit"
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-accent-violet text-white text-[11px] font-medium hover:bg-accent-violet/90 transition-colors"
        >
          <Save className="w-3.5 h-3.5" />
          {field ? 'Salva' : 'Crea'}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="px-3 py-1.5 rounded-lg bg-surface-tertiary border border-border-primary text-[11px] text-text-secondary hover:bg-surface-hover transition-colors"
        >
          Annulla
        </button>
      </div>
    </form>
  );
}
